
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Upload, Download, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import * as XLSX from 'xlsx';
import { companyService } from '@/services/companyService';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { decodeHtmlEntities } from '@/lib/utils';

const PRODUCT_CATEGORY: CategoryKey = 'product';

interface ImportError {
  row: number;
  field: string;
  message: string;
}
interface ValidatedRow {
  rowIndex: number;
  payload: Record<string, any>;
}

interface ImportPreviewData {
  [key: string]: any;
}

const ProductImport = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData[]>([]);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importStatus, setImportStatus] = useState<'idle' | 'preview' | 'success' | 'error'>('idle');
  const [progress, setProgress] = useState<{done: number; total: number}>({ done: 0, total: 0 });
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  useEffect(() => {
    (async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
      } catch (e) {
        // non-blocking
      }
    })();
  }, []);

  // Required columns for product import
  const requiredColumns = [
    'product_id',
    'sku_id',
    'sku_name',
    'brand',
    'category_code',
    'sku_status',
    'warehouse'
  ];

  const allColumns = [
    'product_id', 'sku_id', 'product_type_code', 'category_code', 'brand', 'sku_status',
    'warehouse', 'sku_name', 'sku_name_chi', 'sku_name_sc', 'sku_short_description',
    'sku_short_description_chi', 'sku_short_description_sc', 'sku_long_description',
    'sku_long_description_chi', 'sku_long_description_sc', 'manufactured_country',
    'original_price', 'selling_price', 'packing_spec_eng', 'packing_spec_chi',
    'packing_spec_sc', 'packing_height', 'packing_length', 'packing_depth',
    'packing_dimension_unit', 'weight', 'weight_unit', 'packing_box_type',
    'invisible_flag', 'barcode', 'fine_print_eng', 'fine_print_chi',
    'fine_print_sc', 'oversea_delivery'
  ];

  // Convert raw CSV row into a payload aligned to DB, with basic typing/nullification
  const toNullable = (v: any) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : v);
  const toNumberOrNull = (v: any) => {
    const t = typeof v === 'string' ? v.trim() : v;
    if (t === '' || t === null || t === undefined) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  // Security: Sanitize text to prevent XSS and Excel Formula Injection
  const sanitizeText = (text: string): string => {
    if (!text || typeof text !== 'string') return text;

    let sanitized = text.trim();

    // HTML escape to prevent XSS while preserving content (e.g., "<此日期前最佳：2026.12>" becomes "&lt;此日期前最佳：2026.12&gt;")
    // Escape order matters: & must be escaped first to avoid double-escaping
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    // Prevent Excel Formula Injection by prefixing dangerous formulas with '
    if (/^[=+\-@]/.test(sanitized)) {
      sanitized = "'" + sanitized;
    }

    // Additional security: Limit text length to prevent DoS
    if (sanitized.length > 5000) {
      sanitized = sanitized.substring(0, 5000) + '...';
    }

    return sanitized;
  };

  // Security: Validate numeric fields with range checks
  const validateNumericField = (value: any, fieldName: string, min: number = 0): { isValid: boolean; error?: string } => {
    const num = toNumberOrNull(value);
    if (num === null) return { isValid: true }; // Allow null values

    if (!Number.isFinite(num)) {
      return { isValid: false, error: `${fieldName}必須是有效的數字` };
    }

    if (num < min) {
      return { isValid: false, error: `${fieldName}不能小於 ${min}` };
    }

    // Additional check for extremely large numbers (potential DoS)
    if (num > 999999999) {
      return { isValid: false, error: `${fieldName}數值過大，請檢查數據` };
    }

    return { isValid: true };
  };

  const mapRowToPayload = (row: ImportPreviewData): Record<string, any> => ({
    product_id: toNullable(row.product_id),
    sku_id: toNullable(row.sku_id),
    product_type_code: toNullable(row.product_type_code),
    category_code: toNullable(row.category_code),
    brand: sanitizeText(row.brand),
    sku_status: toNullable(row.sku_status),
    warehouse: toNullable(row.warehouse),
    sku_name: sanitizeText(row.sku_name),
    sku_name_chi: sanitizeText(row.sku_name_chi),
    sku_name_sc: sanitizeText(row.sku_name_sc),
    sku_short_description: sanitizeText(row.sku_short_description),
    sku_short_description_chi: sanitizeText(row.sku_short_description_chi),
    sku_short_description_sc: sanitizeText(row.sku_short_description_sc),
    sku_long_description: sanitizeText(row.sku_long_description),
    sku_long_description_chi: sanitizeText(row.sku_long_description_chi),
    sku_long_description_sc: sanitizeText(row.sku_long_description_sc),
    manufactured_country: sanitizeText(row.manufactured_country),
    original_price: toNumberOrNull(row.original_price),
    selling_price: toNumberOrNull(row.selling_price),
    packing_spec_eng: sanitizeText(row.packing_spec_eng),
    packing_spec_chi: sanitizeText(row.packing_spec_chi),
    packing_spec_sc: sanitizeText(row.packing_spec_sc),
    packing_height: toNumberOrNull(row.packing_height),
    packing_length: toNumberOrNull(row.packing_length),
    packing_depth: toNumberOrNull(row.packing_depth),
    packing_dimension_unit: toNullable(row.packing_dimension_unit),
    weight: toNumberOrNull(row.weight),
    weight_unit: toNullable(row.weight_unit),
    packing_box_type: toNullable(row.packing_box_type),
    invisible_flag: toNullable(row.invisible_flag),
    barcode: sanitizeText(row.barcode),
    fine_print_eng: sanitizeText(row.fine_print_eng),
    fine_print_chi: sanitizeText(row.fine_print_chi),
    fine_print_sc: sanitizeText(row.fine_print_sc),
    oversea_delivery: toNullable(row.oversea_delivery),
  });

  const performAdaptiveUpdate = async (
    basePayload: Record<string, any>,
    baseFilter: Record<string, any>
  ): Promise<{ updated: boolean }> => {
    // Drop timestamp fields that may not exist remotely
    const { updated_at, created_at, ...safeBase } = basePayload as any;
    let workingPayload: Record<string, any> = { ...safeBase };
    let useCompanyFilter = true;
    let attempts = 0;
    let lastError: any = null;

    while (attempts < 8) {
      try {
        let query: any = supabase.from('products').update(workingPayload);
        Object.entries(baseFilter).forEach(([key, value]) => {
          if (key === 'company_id' && !useCompanyFilter) return;
          query = query.eq(key as any, value as any);
        });
        const { data, error } = await query.select('id');
        if (!error) {
          return { updated: !!(data && data.length > 0) };
        }
        lastError = error;
        const msg = String(error.message || '');
        // If company_id column is missing, retry without company filter
        if (msg.includes('company_id') && useCompanyFilter) {
          useCompanyFilter = false;
          attempts++;
          continue;
        }
        // If payload has unknown column, drop and retry
        const missingMatch = msg.match(/'(.*?)'\s+column/);
        const missing = missingMatch?.[1];
        if (missing && Object.prototype.hasOwnProperty.call(workingPayload, missing)) {
          delete (workingPayload as any)[missing];
          attempts++;
          continue;
        }
        break;
      } catch (e: any) {
        lastError = e;
        break;
      }
    }
    if (lastError) throw lastError;
    return { updated: false };
  };

  const verifyWrite = async (
    sku: string,
    pid: string,
    companyIdNum: number
  ): Promise<boolean> => {
    // Try select with company filter, then without if column missing
    try {
      let q: any = supabase.from('products').select('id').limit(1);
      if (sku) q = q.eq('sku_id', sku);
      if (!sku && pid) q = q.eq('product_id', pid);
      q = q.eq('company_id', companyIdNum);
      const { data, error } = await q;
      if (!error) return !!(data && data.length > 0);
      const msg = String(error.message || '');
      if (msg.includes('company_id')) {
        const { data: d2, error: e2 } = await supabase
          .from('products')
          .select('id')
          .limit(1)
          [sku ? 'eq' : 'eq'](sku ? 'sku_id' : 'product_id', sku || pid);
        if (!e2) return !!(d2 && d2.length > 0);
      }
    } catch (_) {}
    return false;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Security: File size limit to prevent DoS attacks
      const MAX_SIZE = 10 * 1024 * 1024; // 10MB limit
      if (file.size > MAX_SIZE) {
        toast.error('檔案太大！請上傳 10MB 以下的檔案。');
        if (event.target) (event.target as HTMLInputElement).value = '';
        return;
      }

      // Require current company code to validate warehouse/store relationship
      const companyCode = (currentCompany?.code || '').toString().trim();
      if (!companyCode) {
        toast.error('未設定當前公司的「公司代碼」，無法驗證 Warehouse/Store。請先到公司管理設定公司代碼。');
        if (event.target) (event.target as HTMLInputElement).value = '';
        return;
      }
      setSelectedFile(file);
      parseFile(file);
    }
  };

  const parseFile = (file: File) => {
    setIsProcessing(true);
    setImportStatus('preview');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const lower = (s: string) => s?.toString().trim().toLowerCase();
        const parsedData: ImportPreviewData[] = [];
        const validationErrors: ImportError[] = [];

        const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
        let headers: string[] = [];
        let dataRows: string[][] = [];

        if (isExcel) {
          const wb = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<any>(sheet, { header: 1, defval: '' }) as any[];
          if (rows.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          headers = (rows[0] as any[]).map((v) => String(v));
          dataRows = rows.slice(1) as any[];
        } else {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((r) => r.trim());
          if (lines.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
          dataRows = lines.slice(1).map((line) => line.split(',').map((v) => v.trim().replace(/"/g, '')));
        }

        // 映射：Excel 顯示名稱 -> 系統欄位
        const headerMap: Record<string, string> = {
          'product id': 'product_id',
          'sku id': 'sku_id',
          'product type code': 'product_type_code',
          'primary category code': 'category_code',
          'category code': 'category_code',
          'brand': 'brand',
          'sku status': 'sku_status',
          'warehouse': 'warehouse',
          'sku name': 'sku_name',
          'sku name chi': 'sku_name_chi',
          'sku name (sc)': 'sku_name_sc',
          'sku short description (eng)': 'sku_short_description',
          'sku short description (chi)': 'sku_short_description_chi',
          'sku short description (sc)': 'sku_short_description_sc',
          'sku long description (eng)': 'sku_long_description',
          'sku long description (chi)': 'sku_long_description_chi',
          'sku long description (sc)': 'sku_long_description_sc',
          'manufactured country': 'manufactured_country',
          'original price': 'original_price',
          'selling price': 'selling_price',
          'packing spec (eng)': 'packing_spec_eng',
          'packing spec (chi)': 'packing_spec_chi',
          'packing spec (sc)': 'packing_spec_sc',
          'packing height': 'packing_height',
          'packing length': 'packing_length',
          'packing depth': 'packing_depth',
          'packing dimension unit': 'packing_dimension_unit',
          'weight': 'weight',
          'weight unit': 'weight_unit',
          'packing box type': 'packing_box_type',
          'invisible flag': 'invisible_flag',
          'barcode': 'barcode',
          'fine print (eng)': 'fine_print_eng',
          'fine print (chi)': 'fine_print_chi',
          'fine print (sc)': 'fine_print_sc',
          'oversea delivery': 'oversea_delivery',
        };

        const normalized = (s: string) => s?.toString().trim().toLowerCase();
        const mappedKeys: (string | null)[] = headers.map((h) => {
          const key = headerMap[normalized(h)];
          // 若本來就已是系統欄位名稱，直接用
          if (!key && allColumns.includes(h)) return h;
          return key || null; // 無對應 -> 忽略
        });

        // 檢查必要欄位是否存在，避免用錯模板
        const hasAllRequired = requiredColumns.every((col) => mappedKeys.includes(col));
        if (!hasAllRequired) {
          const req = requiredColumns.join(', ');
          throw new Error(`找不到必要欄位（需包含：${req}）。請使用產品資料模板。`);
        }

        const expectedCode = (currentCompany?.code || '').toString().trim();

        dataRows.forEach((cols, rowIdx) => {
          if (!cols || cols.length === 0) return;
          const obj: ImportPreviewData = {};
          mappedKeys.forEach((dbKey, ci) => {
            if (!dbKey) return;
            obj[dbKey] = cols[ci] ?? '';
          });

          const hasKey = (obj.sku_id && String(obj.sku_id).trim()) || (obj.product_id && String(obj.product_id).trim());
          if (!hasKey) validationErrors.push({ row: rowIdx + 3, field: 'sku_id|product_id', message: '至少需提供 sku_id 或 product_id' });

          // Security: Enhanced numeric validation with range checks
          const priceValidation = validateNumericField(obj.original_price, '原價');
          if (!priceValidation.isValid) {
            validationErrors.push({ row: rowIdx + 3, field: 'original_price', message: priceValidation.error! });
          }

          const sellingPriceValidation = validateNumericField(obj.selling_price, '售價');
          if (!sellingPriceValidation.isValid) {
            validationErrors.push({ row: rowIdx + 3, field: 'selling_price', message: sellingPriceValidation.error! });
          }

          const weightValidation = validateNumericField(obj.weight, '重量', 0);
          if (!weightValidation.isValid) {
            validationErrors.push({ row: rowIdx + 3, field: 'weight', message: weightValidation.error! });
          }

          // Additional dimension validations
          ['packing_height', 'packing_length', 'packing_depth'].forEach(field => {
            const validation = validateNumericField(obj[field], `${field}尺寸`, 0);
            if (!validation.isValid) {
              validationErrors.push({ row: rowIdx + 3, field, message: validation.error! });
            }
          });

          // 公司核對：warehouse 必須以公司 code 開頭
          const wh = String(obj.warehouse || '').trim();
          if (!wh) {
            validationErrors.push({ row: rowIdx + 3, field: 'warehouse', message: '缺少 Warehouse' });
          } else if (expectedCode && !wh.toUpperCase().startsWith(expectedCode.toUpperCase())) {
            validationErrors.push({ row: rowIdx + 3, field: 'warehouse', message: `倉庫代號需以公司代碼 ${expectedCode} 開頭 (目前: ${wh})` });
          }

          parsedData.push(obj);
        });

        setPreviewData(parsedData);
        setErrors(validationErrors);
        if (validationErrors.length === 0) toast.success(`成功解析 ${parsedData.length} 筆資料，準備匯入`);
        else toast.warning(`解析完成，發現 ${validationErrors.length} 個錯誤需要修正`);
      } catch (error: any) {
        toast.error('檔案解析失敗: ' + error.message);
        setImportStatus('error');
      } finally {
        setIsProcessing(false);
      }
    };

    if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    // 暂时禁用下载模板功能，之后会重新处理
    // TODO: 重新实现模板下载功能
  };

  const handleImport = async () => {
    if (errors.length > 0) {
      toast.error('請先修正所有錯誤再進行匯入');
      return;
    }

    setIsProcessing(true);
    try {
      if (previewData.length === 0) {
        toast.warning('沒有可匯入的資料');
        setIsProcessing(false);
        return;
      }

      setProgress({ done: 0, total: previewData.length });

      const companyIdStr = currentCompany?.id || localStorage.getItem('selectedCompanyId') || '';
      const companyIdNum = Number(String(companyIdStr).trim());
      const companyCode = (currentCompany?.code || '').toString().trim();
      if (!companyIdStr || !Number.isFinite(companyIdNum)) {
        toast.error('找不到當前公司，請先於右上角切換公司後再試');
        setIsProcessing(false);
        return;
      }
      if (!companyCode) {
        toast.error('未設定當前公司的「公司代碼」，無法匯入');
        setIsProcessing(false);
        return;
      }

      // Security: Secure Batch Import - Process in chunks of 50 for better performance and error handling
      const BATCH_SIZE = 50;
      let success = 0;
      let failed = 0;

      // Step 1: Validate and prepare data for batch processing
      const validRows: ValidatedRow[] = [];
      for (let i = 0; i < previewData.length; i++) {
        const row = previewData[i];
        const sku = String(row.sku_id || '').trim();
        const pid = String(row.product_id || '').trim();

        if (!sku && !pid) {
          failed++;
          setErrors(prev => ([
            ...prev,
            { row: i + 2, field: 'sku_id|product_id', message: '至少需提供 sku_id 或 product_id' }
          ]));
          continue;
        }

        // 防禦機制：warehouse 前綴必須符合公司 code
        const wh = String(row.warehouse || '').trim();
        if (companyCode && wh && !wh.toUpperCase().startsWith(companyCode.toUpperCase())) {
          failed++;
          setErrors(prev => ([
            ...prev,
            { row: i + 2, field: 'warehouse', message: `倉庫代號需以公司代碼 ${companyCode} 開頭 (目前: ${wh})` }
          ]));
          continue;
        }

        // Build payload with security sanitization
        const payload = mapRowToPayload(row);
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete (payload as any)[k]);

        const { updated_at: _ua, created_at: _ca, ...rest } = payload as any;
        const upsertPayload: Record<string, any> = {
          ...rest,
          sku_id: sku || rest.sku_id,
          product_id: pid || rest.product_id,
          company_id: companyIdNum,
        };

        validRows.push({ rowIndex: i, payload: upsertPayload });
      }

      // Step 2: Process in batches of 50
      const totalBatches = Math.ceil(validRows.length / BATCH_SIZE);
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIdx = batchIndex * BATCH_SIZE;
        const endIdx = Math.min(startIdx + BATCH_SIZE, validRows.length);
        const batchRows = validRows.slice(startIdx, endIdx);
        const batchPayloads = batchRows.map(row => row.payload);

        try {
          // Batch upsert with adaptive column handling
          let attempts = 0;
          let currentPayloads = [...batchPayloads];

          while (attempts < 8 && currentPayloads.length > 0) {
            const { error: upErr } = await supabase
              .from('products')
              .upsert(currentPayloads, { onConflict: 'company_id,sku_id', ignoreDuplicates: false, defaultToNull: true });

            if (!upErr) break;

            const msg = String(upErr.message || '');
            const match = msg.match(/'(.*?)'\s+column/);
            const missing = match?.[1];

            if (missing) {
              // Remove problematic column from all payloads in this batch
              currentPayloads = currentPayloads.map(payload => {
                if (Object.prototype.hasOwnProperty.call(payload, missing)) {
                  delete (payload as any)[missing];
                }
                return payload;
              });
              attempts++;
              continue;
            }

            // If it's not a missing column error, try alternative conflict key
            const hasSkus = currentPayloads.some(p => p.sku_id);
            if (!hasSkus) {
              const { error: upErr2 } = await supabase
                .from('products')
                .upsert(currentPayloads, { onConflict: 'company_id,product_id', ignoreDuplicates: false, defaultToNull: true });
              if (!upErr2) break;
            }

            throw upErr;
          }

          // Verify batch write results
          const verificationPromises = batchRows.map(async (rowData) => {
            const sku = rowData.payload.sku_id;
            const pid = rowData.payload.product_id;
            const verified = await verifyWrite(sku, pid, companyIdNum);
            return { rowData, verified };
          });

          const verificationResults = await Promise.all(verificationPromises);

          verificationResults.forEach(({ rowData, verified }) => {
            if (verified) {
              success++;
            } else {
              failed++;
              const originalRowIndex = rowData.rowIndex;
              setErrors(prev => ([
                ...prev,
                { row: originalRowIndex + 2, field: 'verify', message: '寫入後驗證未找到該筆記錄，請檢查唯一鍵或 RLS 權限' }
              ]));
            }
          });

        } catch (err: any) {
          // Batch failed - mark all rows in this batch as failed
          failed += batchRows.length;
          batchRows.forEach(rowData => {
            setErrors(prev => ([
              ...prev,
              { row: rowData.rowIndex + 2, field: 'batch_write', message: String(err?.message || err).slice(0, 200) }
            ]));
          });

          // Soft report every batch to avoid spam
          if ((batchIndex + 1) % Math.max(1, Math.floor(totalBatches / 10)) === 0) {
            console.warn('Batch import warning:', err?.message || err);
          }
        }

        // Update progress after each batch
        const processedRows = Math.min((batchIndex + 1) * BATCH_SIZE, validRows.length);
        setProgress({ done: processedRows + (previewData.length - validRows.length), total: previewData.length });
      }

      setImportStatus('success');
      const msg = `匯入完成：成功 ${success} 筆，失敗 ${failed} 筆。`;
      if (failed === 0) toast.success(msg); else toast.warning(msg);
    } catch (error: any) {
      setImportStatus('error');
      toast.error('匯入失敗: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setErrors([]);
    setImportStatus('idle');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', PRODUCT_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <Upload className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '滙入功能（產品資料 - HKTVmall）' : 'Import Product Data (HKTVmall)'}</h1>
          </div>
        </div>

        {/* Upload Section */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">{t('uploadFile')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Template Download */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="text-foreground font-medium">{t('downloadTemplate')}</p>
                    <p className="text-muted-foreground text-sm">{t('templateTip')}</p>
                  </div>
                </div>
                <Button onClick={downloadTemplate} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50">
                  <Download className="w-4 h-4 mr-2" />
                  {t('downloadTemplate')}
                </Button>
              </div>

              {/* File Upload */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="product-import-file"
                    aria-label="選擇匯入檔案（CSV 或 Excel）"
                    title="選擇匯入檔案（CSV 或 Excel）"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={isProcessing}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t('chooseFile')}
                  </Button>
                  
                  {selectedFile && (
                    <div className="flex items-center gap-2">
                      <span className="text-foreground">{selectedFile.name}</span>
                      <Badge variant="outline" className="text-muted-foreground border-border">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resetImport}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {isProcessing && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                    <span>
                      處理中... {progress.done} / {progress.total}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Import Status */}
        {importStatus !== 'idle' && (
          <Card className="bg-card/80 border-border mb-6">
            <CardHeader>
              <div className="flex items-center gap-2">
                {importStatus === 'success' && <CheckCircle className="w-5 h-5 text-green-400" />}
                {importStatus === 'error' && <AlertCircle className="w-5 h-5 text-red-400" />}
                {importStatus === 'preview' && <FileText className="w-5 h-5 text-purple-400" />}
                <CardTitle className="text-foreground">
                  {importStatus === 'success' && (language === 'zh' ? '匯入成功' : 'Import Success')}
                  {importStatus === 'error' && (language === 'zh' ? '匯入失敗' : 'Import Failed')}
                  {importStatus === 'preview' && t('summary')}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-muted-foreground text-sm">{t('totalRecords')}</p>
                    <p className="text-foreground text-2xl font-bold">{previewData.length}</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-muted-foreground text-sm">{t('errorCount')}</p>
                    <p className="text-red-400 text-2xl font-bold">{errors.length}</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-muted-foreground text-sm">{t('importableRecords')}</p>
                    <p className="text-green-400 text-2xl font-bold">{previewData.length - errors.filter((error, index, self) => self.findIndex(e => e.row === error.row) === index).length}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                {importStatus === 'preview' && (
                  <div className="flex gap-4">
                    <Button
                      onClick={handleImport}
                      disabled={errors.length > 0 || isProcessing}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isProcessing ? t('processing') : t('confirmImport')}
                    </Button>
                    <Button onClick={resetImport} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50">
                      {t('cancel')}
                    </Button>
                  </div>
                )}

                {importStatus === 'success' && (
                  <Button onClick={resetImport} className="bg-purple-600 hover:bg-purple-700 text-white">
                    匯入更多資料
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <Card className="bg-card/80 border-border mb-6">
            <CardHeader>
              <CardTitle className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                驗證錯誤 ({errors.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {errors.map((error, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-red-900/20 border border-red-600/30 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <div className="text-sm">
                      <span className="text-red-400 font-medium">第 {error.row} 行</span>
                      <span className="text-muted-foreground mx-2">|</span>
                      <span className="text-muted-foreground">{error.field}</span>
                      <span className="text-muted-foreground mx-2">:</span>
                      <span className="text-foreground">{error.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Preview */}
        {previewData.length > 0 && (
          <Card className="bg-card/80 border-border">
            <CardHeader>
              <CardTitle className="text-foreground">{language === 'zh' ? '資料預覽 (前 10 筆)' : 'Preview (first 10 rows)'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      {/* Custom header order: sku_id, brand, sku_name, warehouse */}
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品ID' : 'SKU ID'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '倉庫' : 'Warehouse'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 10).map((row, index) => {
                      const rowErrors = errors.filter(error => error.row === index + 2);
                      return (
                        <TableRow 
                          key={index} 
                          className={`border-border ${rowErrors.length > 0 ? 'bg-red-900/10' : 'hover:bg-accent/50'}`}
                        >
                          <TableCell className="text-foreground text-sm">{String(row.sku_id || '')}</TableCell>
                          <TableCell className="text-foreground text-sm">{String(row.brand || '')}</TableCell>
                          <TableCell className="text-foreground text-sm">{decodeHtmlEntities(String((language === 'zh' ? (row.sku_name_chi || row.sku_name) : row.sku_name) || ''))}</TableCell>
                          <TableCell className="text-foreground text-sm">{String(row.warehouse || '')}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProductImport;
